import type { ReactNode } from 'react';

import type { StructuredAddress, Transaction } from '@shared/transactions-types';
import { t } from '@luna/i18n';

import type { TransactionPredicate } from '../../predicates/types';

type AddressSelector = (transaction: Transaction) => StructuredAddress | undefined;
type StructuredAddressField = 'department' | 'streetHouse' | 'postCodeTown' | 'country';

type StructuredAddressFieldConfig = {
    field: StructuredAddressField;
    addressProps: Array<keyof StructuredAddress>;
};

export type StructuredDetailFieldConfig<FieldName extends string> = {
    names: Record<StructuredAddressField, FieldName>;
    labelsTranslationKey: string;
    fieldSelector: AddressSelector;
};

type DetailFieldConfig<FieldName = string> = {
    name: FieldName;
    validators: TransactionPredicate[];
    getLabel: (transaction: Transaction) => string;
    render: (transaction: Transaction) => ReactNode;
};

const STRUCTURED_ADDRESS_FIELDS: StructuredAddressFieldConfig[] = [
    {
        field: 'department',
        addressProps: ['department', 'subDepartment'],
    },
    {
        field: 'streetHouse',
        addressProps: ['streetName', 'buildingNumber', 'buildingName', 'floor', 'room', 'postBox'],
    },
    {
        field: 'postCodeTown',
        addressProps: [
            'postCode',
            'townName',
            'townLocationName',
            'districtName',
            'countrySubDivision',
        ],
    },
    {
        field: 'country',
        addressProps: ['countryNation'],
    },
];

const PROP_SEPARATOR = ', ';

export const reduceAddressProps =
    <ReturnType>(selector: AddressSelector, reduce: (props: string[]) => ReturnType) =>
    (addressProps: Array<keyof StructuredAddress>) =>
    (transaction: Transaction): ReturnType => {
        const propValues = addressProps
            .map((prop) => selector(transaction)?.[prop])
            .filter((value): value is string => Boolean(value));
        return reduce(propValues);
    };

export const createStructuredAddressConfig = <FieldName extends string>({
    names,
    labelsTranslationKey,
    fieldSelector,
}: StructuredDetailFieldConfig<FieldName>): Array<DetailFieldConfig<FieldName>> => {
    const hasProps = reduceAddressProps(fieldSelector, (props) => props.length > 0);
    const renderProps = reduceAddressProps(fieldSelector, (props) => props.join(PROP_SEPARATOR));

    return STRUCTURED_ADDRESS_FIELDS.map(({ field, addressProps }) => ({
        name: names[field],
        getLabel: () => t(`${labelsTranslationKey}.${field}`),
        validators: [hasProps(addressProps)],
        render: renderProps(addressProps),
    }));
};
